/**
 * LandingView Component
 * Public landing page for unauthenticated users
 */

import { SEO_LANDING } from '@config/seoConfig';
import { useAuth } from '@contexts/AuthContext';
import { useSEO } from '@hooks/useSEO';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FeaturesSection } from './components/FeaturesSection';
import { HeroSection } from './components/HeroSection';
import { LandingNav } from './components/LandingNav';
import { PricingSection } from './components/PricingSection';

export function LandingView(): ReactElement {
  const { user, loading } = useAuth();

  useSEO(SEO_LANDING);

  // Redirect authenticated users to picks page
  useEffect(() => {
    if (!loading && user) {
      window.location.href = '/picks';
    }
  }, [user, loading]);

  // Show loading or nothing while checking auth
  if (loading || user) {
    return <div className="min-h-screen bg-theme-bg" />;
  }

  return (
    <div data-testid="landing-view" className="min-h-screen bg-theme-bg">
      <LandingNav />

      <main>
        <HeroSection />
        <FeaturesSection />
        <PricingSection />
      </main>

      {/* Footer */}
      <footer className="border-t border-theme-border bg-theme-surface">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-theme-text-tertiary">
              © 2026 Parlay Picker. All rights reserved.
            </div>
            <div className="flex gap-6">
              <Link to="/login" className="text-sm text-theme-text-secondary hover:text-theme-text">
                Login
              </Link>
              <Link
                to="/register"
                className="text-sm text-theme-text-secondary hover:text-theme-text"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
